/**
 * Report Template Engine
 * Generates HTML templates for different report types
 */

import { REPORT_TYPE_DEFINITIONS, FIELD_FORMATS } from '../types/reportTypes';
import { formatDate, formatDuration } from '../../reports.data/utils/dateUtils';

class ReportTemplateEngine {
  constructor() {
    this.baseStyles = this.getBaseStyles();
  }

  /**
   * Generate HTML for a report
   */
  generateReportHTML(reportType, data, options = {}) {
    const reportDef = REPORT_TYPE_DEFINITIONS[reportType];
    if (!reportDef) {
      throw new Error(`Unknown report type: ${reportType}`);
    }

    const { 
      includeStyles = true, 
      printOptimized = true,
      title = reportDef.name,
      subtitle = null
    } = options;

    const html = `
      ${includeStyles ? this.getHTMLHead(title) : ''}
      <body>
        ${this.generateHeader(title, subtitle, data)}
        ${this.generateSummarySection(reportType, data)}
        ${this.generateDataSection(reportType, data)}
        ${printOptimized ? this.generatePrintControls() : ''}
      </body>
      ${includeStyles ? '</html>' : ''}
    `;

    return html;
  }

  /**
   * Generate HTML head with styles
   */
  getHTMLHead(title) {
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${title}</title>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <style>${this.baseStyles}</style>
        </head>
    `;
  }

  /**
   * Generate report header
   */
  generateHeader(title, subtitle, data) {
    const generatedAt = data.generatedAt ? new Date(data.generatedAt).toLocaleString() : new Date().toLocaleString();
    const dateRange = this.getDateRangeString(data.parameters);

    return `
      <div class="header">
        <h1>${title}</h1>
        ${subtitle ? `<h2>${subtitle}</h2>` : ''}
        <p>Generated on ${generatedAt}</p>
        ${dateRange ? `<p>${dateRange}</p>` : ''}
        ${data.fromCache ? '<p class="cache-indicator">⚡ From Cache</p>' : ''}
      </div>
    `;
  }

  /**
   * Generate summary section
   */
  generateSummarySection(reportType, data) {
    // Skip summary section for service-attendees report
    if (reportType === 'service-attendees') {
      return '';
    }

    const reportDef = REPORT_TYPE_DEFINITIONS[reportType];
    if (!reportDef.summaryFields || !data.data) {
      return '';
    }

    const summaryItems = reportDef.summaryFields.map(field => {
      const value = this.getNestedValue(data.data, field.key);
      const formattedValue = this.formatValue(value, field.format);

      return `
        <div class="summary-item">
          <h3>${field.label}</h3>
          <p>${formattedValue}</p>
        </div>
      `;
    }).join('');

    return `
      <div class="summary">
        <div class="summary-grid">
          ${summaryItems}
        </div>
      </div>
    `;
  }

  /**
   * Generate data section (tables, charts, etc.)
   */
  generateDataSection(reportType, data) {
    switch (reportType) {
      case 'daily-log':
        return this.generateDailyLogTable(data.data);
      case 'revenue':
        return this.generateRevenueCharts(data.data);
      case 'employee-performance':
        return this.generateEmployeePerformanceTable(data.data);
      case 'cart-utilization':
        return this.generateCartUtilizationTable(data.data);
      case 'customer-activity':
        return this.generateCustomerActivityTable(data.data);
      case 'service-attendees':
        return this.generateServiceAttendeesTable(data.data);
      default:
        return this.generateGenericDataTable(reportType, data.data);
    }
  }

  /**
   * Generate daily log table
   */
  generateDailyLogTable(data) {
    if (!data.reservations || data.reservations.length === 0) {
      return '<p class="no-data">No reservations found for this date.</p>';
    }

    const tableRows = data.reservations.map(reservation => {
      // Calculate total duration in hours
      const startTime = new Date(reservation.startTime);
      const endTime = reservation.actualReturnTime ? new Date(reservation.actualReturnTime) : (reservation.endTime ? new Date(reservation.endTime) : null);
      let totalDuration = '—';
      if (endTime) {
        const durationMs = endTime - startTime;
        const durationHours = (durationMs / (1000 * 60 * 60)).toFixed(2);
        totalDuration = `${durationHours} hrs`;
      }

      // Sum up payment amounts from all payments
      let cashAmount = 0;
      let creditAmount = 0;
      let surchargeAmount = 0;
      if (reservation.payments && reservation.payments.length > 0) {
        reservation.payments.forEach(payment => {
          cashAmount += payment.cashAmount || 0;
          creditAmount += payment.creditAmount || 0;
          surchargeAmount += payment.surchargeAmount || 0;
        });
      }

      // Calculate total amount (cash + credit, excluding surcharge)
      const totalAmount = cashAmount + creditAmount;

      return `
        <tr>
          <td>${reservation.reservationNumber}</td>
          <td>${reservation.customer?.firstName} ${reservation.customer?.lastName}</td>
          <td>${reservation.inventoryItem?.itemNumber || reservation.golfCart?.cartNumber || reservation.service?.name || 'N/A'}</td>
          <td>${formatDate(reservation.startTime, { includeTime: true })}</td>
          <td>${reservation.endTime ? formatDate(reservation.endTime, { includeTime: true }) : 'Active'}</td>
          <td>${totalDuration}</td>
          <td class="status-${reservation.status?.toLowerCase()}">${reservation.status}</td>
          <td>${this.formatValue(totalAmount, 'currency')}</td>
          <td>${this.formatValue(cashAmount, 'currency')}</td>
          <td>${this.formatValue(creditAmount, 'currency')}</td>
          <td>${this.formatValue(surchargeAmount, 'currency')}</td>
        </tr>
      `;
    }).join('');

    return `
      <div class="data-section">
        <h3>Reservations Details</h3>
        <div class="table-container">
          <table>
            <thead>
              <tr>
                <th>Reservation #</th>
                <th>Customer</th>
                <th>Equipment</th>
                <th>Start Time</th>
                <th>End Time</th>
                <th>Total Duration</th>
                <th>Status</th>
                <th>Total Amount</th>
                <th>Cash Amount</th>
                <th>Credit Amount</th>
                <th>Surcharge Amount</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </div>
      </div>
    `;
  }

  /**
   * Generate employee performance table
   */
  generateEmployeePerformanceTable(data) {
    if (!data.performanceData || data.performanceData.length === 0) {
      return '<p class="no-data">No employee data found for this period.</p>';
    }

    const tableRows = data.performanceData.map(employee => `
      <tr>
        <td>${employee.employee?.name || 'Unknown'}</td>
        <td>${employee.totalReservations || 0}</td>
        <td>${employee.completedReservations || 0}</td>
        <td>${this.formatValue(employee.totalRevenue, 'currency')}</td>
        <td>${this.formatValue(employee.averageRevenue, 'currency')}</td>
        <td>${this.formatValue(employee.completionRate, 'percentage')}</td>
      </tr>
    `).join('');

    return `
      <div class="data-section">
        <h3>Employee Performance Details</h3>
        <div class="table-container">
          <table>
            <thead>
              <tr>
                <th>Employee</th>
                <th>Total Reservations</th>
                <th>Completed</th>
                <th>Total Revenue</th>
                <th>Average Revenue</th>
                <th>Completion Rate</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </div>
      </div>
    `;
  }

  /**
   * Generate customer activity table
   */
  generateCustomerActivityTable(data) {
    if (!data.topCustomers || data.topCustomers.length === 0) {
      return '<p class="no-data">No customer data found for this period.</p>';
    }

    const tableRows = data.topCustomers.map(customer => `
      <tr>
        <td>${customer.name || 'Unknown'}</td>
        <td>${customer.phone || 'N/A'}</td>
        <td>${customer.reservationCount || 0}</td>
        <td>${this.formatValue(customer.totalSpent, 'currency')}</td>
      </tr>
    `).join('');

    return `
      <div class="data-section">
        <h3>Top Customers</h3>
        <div class="table-container">
          <table>
            <thead>
              <tr>
                <th>Customer</th>
                <th>Phone</th>
                <th>Reservations</th>
                <th>Total Spent</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </div>
      </div>
    `;
  }

  /**
   * Generate service attendees table with dynamic columns
   */
  generateServiceAttendeesTable(data) {
    if (!data || !data.data || data.data.length === 0) {
      return '<p class="no-data">No service attendees found for the selected period.</p>';
    }

    // Get columns from the data response
    const columns = data.columns || [];
    
    // Generate table headers
    const tableHeaders = columns.map(col => `<th>${col.label}</th>`).join('');
    
    // Generate table rows
    const tableRows = data.data.map(row => {
      const cells = columns.map(col => {
        const value = row[col.key];
        // Format currency values
        if (col.key === 'amount' || col.key === 'depositAmount') {
          return `<td>${this.formatValue(value, 'currency')}</td>`;
        }
        // Format dates
        if (col.key.includes('Time')) {
          return `<td>${value || 'N/A'}</td>`;
        }
        // Default formatting
        return `<td>${value !== undefined && value !== null ? value : ''}</td>`;
      }).join('');
      return `<tr>${cells}</tr>`;
    }).join('');

    return `
      <div class="data-section">
        <div class="table-container">
          <table class="service-attendees-table">
            <thead>
              <tr>
                ${tableHeaders}
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </div>
        ${data.metadata ? `
          <div class="metadata-section">
            <p class="metadata-info">
              Report includes ${data.metadata.totalAddons || 0} add-on columns and
              ${data.metadata.totalQuestions || 0} questionnaire response columns
            </p>
          </div>
        ` : ''}
      </div>
    `;
  }

  /**
   * Generate generic data table
   */
  generateGenericDataTable(reportType, data) {
    const reportDef = REPORT_TYPE_DEFINITIONS[reportType];
    
    if (!reportDef.columns || !data) {
      return '<p class="no-data">No data available for this report.</p>';
    }

    // This is a fallback for reports without specific table generators
    return `
      <div class="data-section">
        <h3>Report Data</h3>
        <pre class="json-data">${JSON.stringify(data, null, 2)}</pre>
      </div>
    `;
  }

  /**
   * Generate revenue charts placeholder
   */
  generateRevenueCharts(data) {
    return `
      <div class="data-section">
        <h3>Revenue Analysis</h3>
        <p class="chart-placeholder">📊 Chart visualization would be implemented here</p>
        <div class="revenue-breakdown">
          <h4>Payment Breakdown</h4>
          <ul>
            <li>Cash Payments: ${this.formatValue(data.expectedCashPayments, 'currency')}</li>
            <li>Credit Payments: ${this.formatValue(data.expectedCreditPayments, 'currency')}</li>
            <li>Surcharge Collected: ${this.formatValue(data.expectedSurchargePayments, 'currency')}</li>
            <li><strong>Total Expected Revenue: ${this.formatValue(data.expectedRevenue, 'currency')}</strong></li>
          </ul>
        </div>
      </div>
    `;
  }

  /**
   * Generate cart utilization table
   */
  generateCartUtilizationTable(data) {
    return `
      <div class="data-section">
        <h3>Equipment Utilization Analysis</h3>
        <p class="chart-placeholder">📊 Utilization charts and detailed equipment breakdown would be implemented here</p>
      </div>
    `;
  }

  /**
   * Generate print controls
   */
  generatePrintControls() {
    return `
      <div class="no-print print-controls">
        <button onclick="window.print()">🖨️ Print Report</button>
        <button onclick="window.close()">✖️ Close</button>
      </div>
    `;
  }

  /**
   * Format values based on type
   */
  formatValue(value, format) {
    if (value === null || value === undefined) {
      return '—';
    }

    switch (format) {
      case FIELD_FORMATS.CURRENCY:
        return `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      case FIELD_FORMATS.PERCENTAGE:
        return `${Number(value).toFixed(1)}%`;
      case FIELD_FORMATS.NUMBER:
        return Number(value).toLocaleString();
      case FIELD_FORMATS.DATE:
        return formatDate(value);
      case FIELD_FORMATS.DATETIME:
        return formatDate(value, { includeTime: true });
      default:
        return String(value);
    }
  }

  /**
   * Get nested object value safely
   */
  getNestedValue(obj, path) {
    return path.split('.').reduce((current, key) => current && current[key], obj);
  }

  /**
   * Get date range string for header
   */
  getDateRangeString(parameters) {
    if (!parameters) return '';
    
    const { date, startDate, endDate } = parameters;
    
    if (date) {
      return `Date: ${formatDate(date)}`;
    }
    
    if (startDate && endDate) {
      return `Period: ${formatDate(startDate)} to ${formatDate(endDate)}`;
    }
    
    return '';
  }

  /**
   * Get base CSS styles for reports
   */
  getBaseStyles() {
    return `
      body { 
        font-family: Arial, sans-serif; 
        margin: 20px; 
        color: #333;
      }
      
      .header { 
        text-align: center; 
        margin-bottom: 30px; 
        border-bottom: 2px solid #eee;
        padding-bottom: 20px;
      }
      
      .header h1 {
        margin: 0 0 10px 0;
        color: #2c3e50;
      }
      
      .cache-indicator {
        color: #3498db;
        font-weight: bold;
      }
      
      .summary { 
        margin-bottom: 30px; 
      }
      
      .summary-grid { 
        display: grid; 
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); 
        gap: 20px; 
        margin-bottom: 20px; 
      }
      
      .summary-item { 
        background: #f8f9fa; 
        padding: 15px; 
        border-radius: 8px; 
        text-align: center;
        border: 1px solid #dee2e6;
      }
      
      .summary-item h3 { 
        margin: 0 0 10px 0; 
        color: #495057; 
        font-size: 14px;
      }
      
      .summary-item p { 
        margin: 0; 
        font-size: 24px; 
        font-weight: bold; 
        color: #212529; 
      }
      
      .data-section {
        margin-bottom: 30px;
      }
      
      .data-section h3 {
        border-bottom: 1px solid #dee2e6;
        padding-bottom: 10px;
        margin-bottom: 20px;
      }
      
      .table-container {
        overflow-x: auto;
        border: 1px solid #dee2e6;
        border-radius: 8px;
      }
      
      .table-container {
        overflow-x: auto;
        max-width: 100%;
        margin-bottom: 20px;
      }

      table {
        width: auto;
        min-width: 100%;
        border-collapse: collapse;
        table-layout: auto;
      }
      
      th, td {
        border: 1px solid #dee2e6;
        padding: 12px 8px;
        text-align: left;
        vertical-align: top;
        white-space: nowrap;
      }

      /* Service Attendees specific styles */
      .service-attendees-table {
        width: auto !important;
        min-width: 1600px; /* Ensure minimum width for readability */
        table-layout: fixed; /* Use fixed layout for consistent column widths */
      }

      .service-attendees-table th,
      .service-attendees-table td {
        padding: 8px 6px;
        font-size: 12px;
        white-space: normal;
        word-wrap: break-word;
      }

      /* Fixed width for specific columns - optimized for browser viewing */
      .service-attendees-table th:nth-child(1),
      .service-attendees-table td:nth-child(1) { width: 120px; min-width: 120px; } /* Service */

      .service-attendees-table th:nth-child(2),
      .service-attendees-table td:nth-child(2) { width: 100px; min-width: 100px; } /* First Name */

      .service-attendees-table th:nth-child(3),
      .service-attendees-table td:nth-child(3) { width: 100px; min-width: 100px; } /* Last Name */

      .service-attendees-table th:nth-child(4),
      .service-attendees-table td:nth-child(4) { width: 80px; min-width: 80px; } /* Res ID */

      .service-attendees-table th:nth-child(5),
      .service-attendees-table td:nth-child(5) { width: 120px; min-width: 120px; } /* Phone */

      .service-attendees-table th:nth-child(6),
      .service-attendees-table td:nth-child(6) { width: 80px; min-width: 80px; text-align: center; } /* # Participants */

      .service-attendees-table th:nth-child(7),
      .service-attendees-table td:nth-child(7) { width: 200px; min-width: 200px; } /* Email */

      .service-attendees-table th:nth-child(8),
      .service-attendees-table td:nth-child(8) { width: 100px; min-width: 100px; } /* Payment */

      .service-attendees-table th:nth-child(9),
      .service-attendees-table td:nth-child(9) { width: 80px; min-width: 80px; text-align: right; } /* Amount */

      .service-attendees-table th:nth-child(10),
      .service-attendees-table td:nth-child(10) { width: 80px; min-width: 80px; } /* Source */

      /* Dynamic columns (questionnaire/addons) get flexible width */
      .service-attendees-table th:nth-child(n+11),
      .service-attendees-table td:nth-child(n+11) {
        width: auto;
        min-width: 100px;
        max-width: 200px;
      }
      
      th { 
        background-color: #f8f9fa; 
        font-weight: bold; 
        font-size: 13px;
        color: #495057;
      }
      
      td {
        font-size: 13px;
      }
      
      .status-active { color: #0d6efd; }
      .status-completed { color: #198754; }
      .status-cancelled { color: #dc3545; }
      
      .no-data {
        text-align: center;
        color: #6c757d;
        font-style: italic;
        padding: 40px;
      }
      
      .chart-placeholder {
        background: #f8f9fa;
        padding: 40px;
        text-align: center;
        border: 2px dashed #dee2e6;
        border-radius: 8px;
        color: #6c757d;
        margin: 20px 0;
      }
      
      .revenue-breakdown {
        background: #fff;
        border: 1px solid #dee2e6;
        border-radius: 8px;
        padding: 20px;
      }
      
      .revenue-breakdown ul {
        list-style: none;
        padding: 0;
      }
      
      .revenue-breakdown li {
        padding: 5px 0;
        border-bottom: 1px solid #eee;
      }
      
      .json-data {
        background: #f8f9fa;
        border: 1px solid #dee2e6;
        border-radius: 4px;
        padding: 15px;
        font-size: 12px;
        overflow-x: auto;
      }
      
      .print-controls {
        position: fixed;
        bottom: 20px;
        right: 20px;
        display: flex;
        gap: 10px;
      }
      
      .print-controls button {
        background: #007bff;
        color: white;
        border: none;
        padding: 10px 15px;
        border-radius: 5px;
        cursor: pointer;
        font-weight: bold;
      }
      
      .print-controls button:hover {
        background: #0056b3;
      }
      
      @media print {
        @page {
          margin: 0.4in 0.3in;
        }

        body {
          margin: 0;
          font-size: 10pt;
        }

        .no-print {
          display: none !important;
        }

        .table-container {
          border: none;
          overflow: visible;
          margin: 0;
        }

        table {
          page-break-inside: auto;
          table-layout: fixed;
          width: 100%;
          font-size: 9pt;
          margin-bottom: 10px;
        }

        th, td {
          padding: 2px 1px;
          font-size: 6.5pt;
          word-wrap: break-word;
          overflow-wrap: break-word;
          white-space: normal;
          vertical-align: top;
          line-height: 1.1;
        }

        th {
          background-color: #f0f0f0 !important;
          font-weight: bold;
          font-size: 6.5pt;
        }

        /* Service Attendees table - optimized for print/PDF */
        .service-attendees-table {
          width: 100% !important;
          min-width: auto !important;
          table-layout: auto !important;
        }

        /* Remove horizontal scroll for print/PDF */
        .table-container {
          overflow-x: visible !important;
          overflow: visible !important;
          max-width: none !important;
        }

        .data-section .table-container {
          overflow-x: visible !important;
          overflow: visible !important;
        }

        body, html {
          overflow-x: visible !important;
        }

        /* Minimal print column widths - maximize space for dynamic columns */
        .service-attendees-table th:nth-child(1),
        .service-attendees-table td:nth-child(1) { width: 4%; } /* Service */
        .service-attendees-table th:nth-child(2),
        .service-attendees-table td:nth-child(2) { width: 4%; } /* First Name */
        .service-attendees-table th:nth-child(3),
        .service-attendees-table td:nth-child(3) { width: 4%; } /* Last Name */
        .service-attendees-table th:nth-child(4),
        .service-attendees-table td:nth-child(4) { width: 3%; } /* Res ID */
        .service-attendees-table th:nth-child(5),
        .service-attendees-table td:nth-child(5) { width: 5%; } /* Phone */
        .service-attendees-table th:nth-child(6),
        .service-attendees-table td:nth-child(6) { width: 2%; text-align: center; } /* # Participants */
        .service-attendees-table th:nth-child(7),
        .service-attendees-table td:nth-child(7) { width: 6%; } /* Email */
        .service-attendees-table th:nth-child(8),
        .service-attendees-table td:nth-child(8) { width: 3%; } /* Payment */
        .service-attendees-table th:nth-child(9),
        .service-attendees-table td:nth-child(9) { width: 4%; text-align: right; } /* Amount */
        .service-attendees-table th:nth-child(10),
        .service-attendees-table td:nth-child(10) { width: 2%; } /* Source */

        /* Dynamic columns (questionnaire/addons) - generous space for headers and content */
        .service-attendees-table th:nth-child(n+11),
        .service-attendees-table td:nth-child(n+11) {
          width: auto;
          min-width: 8%;
          max-width: 15%;
        }

        tr {
          page-break-inside: avoid;
          page-break-after: auto;
        }

        thead {
          display: table-header-group;
        }

        .summary-grid {
          display: block;
          margin-bottom: 20px;
        }

        .summary-item {
          display: inline-block;
          width: 150px;
          margin-right: 5px;
          margin-bottom: 5px;
          font-size: 9pt;
          padding: 8px;
        }

        .summary-item h3 {
          font-size: 10pt;
        }

        .summary-item p {
          font-size: 12pt;
        }

        h1 {
          font-size: 16pt;
          margin-bottom: 10px;
        }

        h2, h3 {
          font-size: 12pt;
          margin-bottom: 8px;
        }
      }
      
      @media screen and (max-width: 768px) {
        .summary-grid {
          grid-template-columns: 1fr;
        }
        
        table {
          font-size: 12px;
        }
        
        th, td {
          padding: 8px 4px;
        }
      }
    `;
  }
}

export default new ReportTemplateEngine();